import React, { createContext, useContext, useMemo, useEffect } from 'react'
import { Configuration as V1Configuration } from '../api/generated/v1/configuration-export'
import * as apiV1 from '../api/generated/v1/api'
import { useAuth } from './AuthContext'
import axios from 'axios'
import { getApiBaseUrl, API_V1_PREFIX } from '../config/constants'

interface ApiClients {
	UserApi: apiV1.UserApi
	AuthApi: apiV1.AuthApi
	UserprofileApi: apiV1.UserprofileApi
	TeamApi: apiV1.TeamApi
	BalanceApi: apiV1.BalanceApi
	MachineApi: apiV1.MachineApi
	NotificationsApi: apiV1.NotificationsApi
	// v2?: typeof apiV2 // ready for future extension
}

const ApiContext = createContext<ApiClients | undefined>(undefined)

export function ApiProvider({ children }: { children: React.ReactNode }) {
	const { accessToken, refreshToken, logout } = useAuth()

	// Global axios instance (used by generated clients) interceptors: refresh token & retry once
	useEffect(() => {
		const axiosInstance = axios;
		let isRefreshing = false;
		let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

		const processQueue = (error: any, token: string | null) => {
			failedQueue.forEach(prom => {
				if (error) prom.reject(error);
				else prom.resolve(token);
			});
			failedQueue = [];
		};

		const interceptor = axiosInstance.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;

				if (error.response && error.response.status === 401 && !originalRequest?._retry) {
					if (originalRequest?.url?.includes('/auth/') || originalRequest?.url?.includes('/login')) {
						// auth endpoints, do not retry
						logout();
						return Promise.reject(error);
					}

					if (isRefreshing) {
						return new Promise((resolve, reject) => {
							failedQueue.push({
								resolve: (token: string) => {
									if (token) originalRequest.headers['Authorization'] = `Bearer ${token}`;
									resolve(axiosInstance(originalRequest));
								},
								reject,
							});
						});
					}

					originalRequest._retry = true;
					isRefreshing = true;
					try {
						const newToken = await refreshToken();
						if (!newToken) {
							logout();
							return Promise.reject(error);
						}

						axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
						processQueue(null, newToken);

						originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
						return axiosInstance(originalRequest);
					} catch (err) {
						processQueue(err, null);
						logout();
						return Promise.reject(err);
					} finally {
						isRefreshing = false;
					}
				}

				return Promise.reject(error);
			}
		);

		return () => {
			axiosInstance.interceptors.response.eject(interceptor);
		};
	}, [refreshToken, logout]);

	// Update Authorization header when token changes
	useEffect(() => {
		if (accessToken) {
			axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
		} else {
			delete axios.defaults.headers.common['Authorization'];
		}
	}, [accessToken]);

	const apiBaseUrl = getApiBaseUrl()
	const v1Config = useMemo(() => new V1Configuration({
		basePath: `${apiBaseUrl}${API_V1_PREFIX}`,
		accessToken: accessToken || undefined,
	}), [accessToken, apiBaseUrl])

	const apiClients = useMemo(() => {
		return {
			UserApi: new apiV1.UserApi(v1Config),
			AuthApi: new apiV1.AuthApi(v1Config),
			UserprofileApi: new apiV1.UserprofileApi(v1Config),
			TeamApi: new apiV1.TeamApi(v1Config),
			BalanceApi: new apiV1.BalanceApi(v1Config),
			MachineApi: new apiV1.MachineApi(v1Config),
			NotificationsApi: new apiV1.NotificationsApi(v1Config),
			// v2: ...
		}
	}, [v1Config])

	return (
		<ApiContext.Provider value={apiClients}>
			{children}
		</ApiContext.Provider>
	)
}

export function useApi() {
	const context = useContext(ApiContext)
	if (!context) {
		throw new Error('useApi must be used within an ApiProvider')
	}
	return context
} 